import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { AlertCircle, ArrowDown, ArrowUp, Info, TrendingDown, TrendingUp } from 'lucide-react';
import { useCreateOrder } from '@/api/orders/use-orders';
import { useCreateCrmOrder } from '@/api/orders/use-crm-orders';
import { Instrument } from '@/api/types';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useDebouncedCalculation } from '@/hooks/useDebouncedCalculation';
import { formatErrorForDisplay } from '@/utils/errorHandling';
import {
    clampOrderLeverage,
    getDefaultLeverage,
    getMaxAllowedOrderLeverage,
    getOrderLeverage,
    type InstrumentType,
} from '@/utils/leverageOptions';
import { formatInstrumentPrice, formatCurrencyAmount } from '@/utils/formatters';
import { useForm } from 'react-hook-form';
import LeverageSelect from '@/components/ui/LeverageSelect';
import { SearchableSelect } from '@/components/ui/searchable-select';

interface OrderFormAccount {
    id: string;
    account_number?: number | string;
    base_currency: string;
    balance: number;
    free_margin: number;
    leverage: number;
}

interface OrderFormContentProps {
    onClose: () => void;
    selectedPair: string;
    selectedInstrumentId?: string;
    realTimePrice?: { bid: number; ask: number; symbol?: string };
    selectedAccount?: OrderFormAccount | null;
    tradingAccounts?: OrderFormAccount[];
    onOrderSuccess?: () => void;
    isPricesLoading?: boolean;
    pricesError?: string | null;
    instrumentType?: InstrumentType;
    instrumentData?: {
        min_lot: number;
        max_lot: number;
        max_leverage: number;
        leverage: number;
    };
    direction: 'buy' | 'sell';
    variant?: 'trader' | 'crm';
    customerId?: string;
    instruments?: Instrument[];
    instrumentsLoading?: boolean;
    onInstrumentChange?: (instrumentId: string) => void;
    onInstrumentSearch?: (search: string) => void;
    allowInstrumentPriceFallback?: boolean;
    submitLabel?: string;
}

interface OrderFormData {
    volume: string;
    price: string;
    stopLoss: string;
    takeProfit: string;
    orderType: 'market' | 'limit' | 'stop' | 'stop_limit';
    stopType: string;
    stopPrice: string;
    postOnly: boolean;
    reduceOnly: boolean;
}

export const OrderFormContent = ({
    onClose,
    selectedPair,
    selectedInstrumentId,
    realTimePrice,
    selectedAccount: initialSelectedAccount,
    tradingAccounts: accountsList,
    onOrderSuccess,
    isPricesLoading = false,
    pricesError = null,
    instrumentType = 'forex',
    instrumentData,
    direction,
    variant = 'trader',
    customerId,
    instruments,
    instrumentsLoading = false,
    onInstrumentChange,
    onInstrumentSearch,
    allowInstrumentPriceFallback = false,
    submitLabel,
}: OrderFormContentProps) => {
    const { t } = useTranslation('trading');
    const queryClient = useQueryClient();
    const createOrderMutation = useCreateOrder();
    const createCrmOrderMutation = useCreateCrmOrder();
    const { enqueueSnackbar } = useSnackbar();
    const isCrm = variant === 'crm';
    const isSubmitting = isCrm ? createCrmOrderMutation.isPending : createOrderMutation.isPending;

    // Account selection state
    const [selectedAccount, setSelectedAccount] = React.useState<OrderFormAccount | null>(
        initialSelectedAccount || null
    );

    // Update selected account when accounts list or initial account changes
    React.useEffect(() => {
        if (accountsList && accountsList.length > 0) {
            const accountToUse =
                initialSelectedAccount &&
                accountsList.find(acc => acc.id === initialSelectedAccount.id)
                    ? initialSelectedAccount
                    : accountsList[0];
            setSelectedAccount(accountToUse);
        } else if (initialSelectedAccount) {
            setSelectedAccount(initialSelectedAccount);
        } else {
            setSelectedAccount(null);
        }
    }, [accountsList, initialSelectedAccount]);

    // Get instrument limits for validation
    const minLot = instrumentData?.min_lot || 0.01;
    const maxLot = instrumentData?.max_lot || 100;
    const instrumentMaxLeverage =
        instrumentData?.max_leverage ||
        instrumentData?.leverage ||
        getDefaultLeverage(instrumentType);
    const accountLeverage = selectedAccount?.leverage ?? instrumentMaxLeverage;
    const maxAllowedLeverage = getMaxAllowedOrderLeverage(instrumentMaxLeverage, accountLeverage);
    const orderLeverage = getOrderLeverage(instrumentMaxLeverage, accountLeverage);

    const [selectedLeverage, setSelectedLeverage] = React.useState(orderLeverage);

    React.useEffect(() => {
        setSelectedLeverage(orderLeverage);
    }, [orderLeverage, selectedInstrumentId, selectedAccount?.id]);

    // Use react-hook-form for form management
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm<OrderFormData>({
        defaultValues: {
            volume: minLot.toString(),
            price: '',
            stopLoss: '',
            takeProfit: '',
            orderType: 'market',
            stopType: 'no',
            stopPrice: '',
            postOnly: false,
            reduceOnly: false,
        },
        mode: 'onChange',
    });

    // Watch form values for calculations
    const watchedValues = watch();
    const { volume, price, orderType } = watchedValues;

    React.useEffect(() => {
        if (isCrm && selectedInstrumentId) {
            setValue('volume', minLot.toString());
        }
    }, [isCrm, selectedInstrumentId, minLot, setValue]);

    // Use custom hook for debounced calculations
    const { calculation, calculateOrder, clearCalculation, isCalculating } =
        useDebouncedCalculation(variant);

    // Trigger calculation when inputs change
    React.useEffect(() => {
        if (!selectedInstrumentId || !volume || !selectedAccount) {
            clearCalculation();
            return;
        }

        const lotSize = parseFloat(volume);

        if (isNaN(lotSize) || lotSize <= 0) {
            clearCalculation();
            return;
        }

        let priceValue: number | undefined;
        if (orderType === 'limit' && price) {
            const parsedPrice = parseFloat(price);
            if (!isNaN(parsedPrice) && parsedPrice > 0) {
                priceValue = parsedPrice;
            }
        }

        calculateOrder({
            instrument_id: selectedInstrumentId,
            trading_account_id: selectedAccount.id,
            side: direction,
            lot_size: lotSize,
            leverage: clampOrderLeverage(selectedLeverage, maxAllowedLeverage),
            price: priceValue,
        });
    }, [
        selectedInstrumentId,
        volume,
        selectedAccount,
        orderType,
        direction,
        selectedLeverage,
        maxAllowedLeverage,
        calculateOrder,
        clearCalculation,
        price,
        // Re-calculate when the live price changes (market orders use current
        // price server-side; margin/cost preview must stay in sync with ticks)
        realTimePrice?.bid,
        realTimePrice?.ask,
    ]);

    const hasValidPriceData = Boolean(realTimePrice && realTimePrice.bid && realTimePrice.ask);
    const canSubmitWithPrice = hasValidPriceData || allowInstrumentPriceFallback;

    const onSubmit = async (data: OrderFormData) => {
        try {
            if (isCrm && !selectedInstrumentId) {
                enqueueSnackbar('Please select an instrument', { variant: 'error' });
                return;
            }

            if (!data.volume || !canSubmitWithPrice) {
                enqueueSnackbar(t('enterVolumeAndPrice'), { variant: 'error' });
                return;
            }

            if (data.orderType === 'limit' && !data.price) {
                enqueueSnackbar(t('enterLimitPrice'), { variant: 'error' });
                return;
            }

            const lotSize = parseFloat(data.volume);
            if (lotSize < minLot) {
                enqueueSnackbar(t('minLotSize', { min: minLot }), { variant: 'error' });
                return;
            }
            if (lotSize > maxLot) {
                enqueueSnackbar(t('maxLotSize', { max: maxLot }), { variant: 'error' });
                return;
            }

            if (
                selectedAccount &&
                calculation?.calculations?.margin_required &&
                calculation.calculations.margin_required > selectedAccount.free_margin
            ) {
                enqueueSnackbar(t('insufficientMargin'), { variant: 'error' });
                return;
            }

            if (!selectedAccount) {
                enqueueSnackbar(t('selectTradingAccount'), { variant: 'error' });
                return;
            }

            const orderInput = {
                instrument_id: selectedInstrumentId!,
                trading_account_id: selectedAccount.id,
                side: direction,
                type: data.orderType,
                lot_size: parseFloat(data.volume),
                price:
                    data.orderType === 'limit' || data.orderType === 'stop_limit'
                        ? parseFloat(data.price)
                        : undefined,
                stop_price:
                    data.orderType === 'stop' || data.orderType === 'stop_limit'
                        ? parseFloat(data.stopPrice)
                        : undefined,
                stop_loss_price: data.stopLoss ? parseFloat(data.stopLoss) : undefined,
                take_profit_price: data.takeProfit ? parseFloat(data.takeProfit) : undefined,
                leverage: clampOrderLeverage(selectedLeverage, maxAllowedLeverage),
                expires_at: undefined,
                post_only: data.postOnly,
                reduce_only: data.reduceOnly,
                margin_type: 'isolated' as const,
            };

            if (isCrm) {
                if (!customerId) {
                    enqueueSnackbar('Customer is required', { variant: 'error' });
                    return;
                }

                await createCrmOrderMutation.mutateAsync({
                    ...orderInput,
                    customer_id: customerId,
                });

                queryClient.invalidateQueries({ queryKey: ['crm-trades'] });
                queryClient.invalidateQueries({ queryKey: ['trade-history'] });
                queryClient.invalidateQueries({
                    queryKey: ['customer-trading-accounts', customerId],
                });
            } else {
                await createOrderMutation.mutateAsync(orderInput);

                queryClient.invalidateQueries({ queryKey: ['trader-trades'] });
                queryClient.invalidateQueries({ queryKey: ['trader-trades-summary'] });
            }

            enqueueSnackbar(
                isCrm
                    ? 'Trade created successfully!'
                    : t('orderPlacedSuccess', {
                          direction: direction === 'buy' ? t('buy') : t('sell'),
                      }),
                {
                    variant: 'success',
                }
            );

            reset({
                volume: minLot.toString(),
                price: '',
                stopLoss: '',
                takeProfit: '',
                orderType: 'market',
                stopType: 'no',
                stopPrice: '',
                postOnly: false,
                reduceOnly: false,
            });

            if (!isCrm) {
                queryClient.invalidateQueries({ queryKey: ['trader-trades'] });
                queryClient.invalidateQueries({ queryKey: ['trader-trades-summary'] });
            }
            if (onOrderSuccess) {
                onOrderSuccess();
            }
            onClose();
        } catch (error) {
            console.error('Error submitting order:', error);
            const errorResult = formatErrorForDisplay(error);
            enqueueSnackbar(t('failedToPlaceOrder', { message: errorResult.message }), {
                variant: 'error',
            });
        }
    };

    const handlePriceIncrement = (
        field: 'price' | 'stopLoss' | 'takeProfit' | 'stopPrice',
        increment: boolean
    ) => {
        const currentValue = watch(field);
        const currentPrice = parseFloat(currentValue) || 0;
        const incrementValue = increment ? 0.00001 : -0.00001;
        const newPrice = Math.max(0, currentPrice + incrementValue);
        setValue(field, newPrice.toFixed(5));
    };

    const currentPrice = hasValidPriceData
        ? direction === 'buy'
            ? realTimePrice.ask
            : realTimePrice.bid
        : 0;

    const instrumentOptions = React.useMemo(
        () =>
            (instruments || []).map(instrument => ({
                value: instrument.id,
                label: instrument.symbol,
                description: instrument.instrument_type?.primary_type,
            })),
        [instruments]
    );

    return (
        <div className="space-y-4">
            {/* Instrument Selection (CRM) */}
            {isCrm && instruments && onInstrumentChange && (
                <div>
                    <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                        Instrument
                    </Label>
                    <SearchableSelect
                        options={instrumentOptions}
                        value={selectedInstrumentId || ''}
                        onValueChange={onInstrumentChange}
                        disabled={instrumentsLoading && !instrumentOptions.length}
                        placeholder={
                            instrumentsLoading
                                ? 'Loading instruments...'
                                : 'Select trading instrument'
                        }
                        searchPlaceholder="Search instruments..."
                        emptyMessage="No instrument found."
                        className="h-10 text-sm"
                        onSearch={onInstrumentSearch}
                        loading={instrumentsLoading}
                    />
                </div>
            )}

            {/* Direction Indicator */}
            <div
                className={`text-center p-3 rounded-lg ${
                    direction === 'buy'
                        ? 'bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800'
                }`}
            >
                <div
                    className={`flex items-center justify-center space-x-2 ${
                        direction === 'buy'
                            ? 'text-green-700 dark:text-green-300'
                            : 'text-red-700 dark:text-red-300'
                    }`}
                >
                    {direction === 'buy' ? (
                        <TrendingUp className="h-5 w-5" />
                    ) : (
                        <TrendingDown className="h-5 w-5" />
                    )}
                    <span className="text-lg font-semibold">
                        {direction === 'buy' ? t('buy') : t('sell')} {selectedPair}
                    </span>
                </div>
                {hasValidPriceData && (
                    <div className="text-sm mt-1">
                        {t('priceLabel')}:{' '}
                        {formatInstrumentPrice(
                            direction === 'buy' ? realTimePrice.ask : realTimePrice.bid,
                            realTimePrice.symbol ? { symbol: realTimePrice.symbol } : undefined
                        )}
                    </div>
                )}
            </div>

            {/* Trading Account Selection */}
            {accountsList && accountsList.length > 0 && (
                <div>
                    <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                        {t('tradingAccountLabel')}
                    </Label>
                    <Select
                        value={selectedAccount?.id || ''}
                        onValueChange={value => {
                            const account = accountsList.find(acc => acc.id === value);
                            if (account) {
                                setSelectedAccount(account);
                            }
                        }}
                        disabled={accountsList.length === 1}
                    >
                        <SelectTrigger className="h-10 text-sm">
                            <SelectValue placeholder={t('selectAccountPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                            {accountsList.map(account => (
                                <SelectItem key={account.id} value={account.id}>
                                    <div className="flex items-center justify-between w-full">
                                        <span>
                                            {account.account_number
                                                ? `#${account.account_number}`
                                                : account.base_currency}
                                        </span>
                                        <span className="text-xs text-muted-foreground ml-2">
                                            {formatCurrencyAmount(
                                                account.balance,
                                                account.base_currency
                                            )}
                                        </span>
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            {/* Order Type */}
            <div>
                <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                    {t('orderType')}
                </Label>
                <Select
                    value={orderType}
                    onValueChange={(value: 'market' | 'limit' | 'stop' | 'stop_limit') =>
                        setValue('orderType', value)
                    }
                >
                    <SelectTrigger className="h-10 text-sm">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="market">{t('orderTypeMarket')}</SelectItem>
                        <SelectItem value="limit">{t('orderTypeLimit')}</SelectItem>
                        <SelectItem value="stop">{t('orderTypeStop')}</SelectItem>
                        <SelectItem value="stop_limit">{t('orderTypeStopLimit')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Leverage Selection */}
            <div>
                <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                    {t('leverage')}
                </Label>
                <LeverageSelect
                    value={String(clampOrderLeverage(selectedLeverage, maxAllowedLeverage))}
                    onValueChange={value => setSelectedLeverage(parseInt(value, 10))}
                    instrumentType={instrumentType}
                    maxLeverage={maxAllowedLeverage}
                    defaultLeverage={orderLeverage}
                    placeholder={t('selectLeverage')}
                    className="h-10 text-sm"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                    {t('maxLeverageDisplay', { max: maxAllowedLeverage })}
                </p>
            </div>

            {/* Advanced Options */}
            <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="postOnly"
                        {...register('postOnly')}
                        className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                    />
                    <Label htmlFor="postOnly" className="text-sm text-muted-foreground">
                        {t('postOnly')}
                    </Label>
                </div>
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="reduceOnly"
                        {...register('reduceOnly')}
                        className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                    />
                    <Label htmlFor="reduceOnly" className="text-sm text-muted-foreground">
                        {t('reduceOnly')}
                    </Label>
                </div>
            </div>

            {/* Volume Input */}
            <div>
                <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                    {t('volumeLots')}
                </Label>
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        type="number"
                        step={minLot.toString()}
                        min={minLot}
                        max={maxLot}
                        placeholder={minLot.toString()}
                        {...register('volume', {
                            required: t('volumeRequired'),
                            min: { value: minLot, message: t('volumeMin', { min: minLot }) },
                            max: { value: maxLot, message: t('volumeMax', { max: maxLot }) },
                        })}
                        className="h-10 text-sm"
                    />
                    <div className="grid grid-cols-2 gap-2">
                        {[minLot, minLot * 5, minLot * 10, minLot * 25].map(vol => (
                            <Button
                                key={vol}
                                type="button"
                                onClick={() => setValue('volume', vol.toString())}
                                variant={volume === vol.toString() ? 'default' : 'outline'}
                                size="sm"
                                className="h-10 text-sm"
                            >
                                {vol}
                            </Button>
                        ))}
                    </div>
                </div>
                {errors.volume && (
                    <div className="flex items-center space-x-1 mt-1 text-sm text-red-500">
                        <AlertCircle className="h-4 w-4" />
                        <span>{errors.volume.message}</span>
                    </div>
                )}
            </div>

            {/* Market Order Price Info */}
            {orderType === 'market' && (
                <div className="p-3 bg-primary/10 dark:bg-primary/20 rounded-lg border border-primary/20 dark:border-primary/30">
                    <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-primary/100 rounded-full"></div>
                        <p className="text-sm text-primary dark:text-primary font-medium">
                            {t('marketOrder')}
                        </p>
                    </div>
                    <p className="text-sm text-primary dark:text-primary mt-1">
                        {t('marketOrderDescription')}
                    </p>
                </div>
            )}

            {/* Price Input (for Limit Orders) */}
            {(orderType === 'limit' || orderType === 'stop_limit') && (
                <div>
                    <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                        {t('priceLabel')}
                    </Label>
                    <div className="relative">
                        <Input
                            type="number"
                            step="0.00001"
                            placeholder="0.00000"
                            {...register('price', {
                                required:
                                    orderType === 'limit' || orderType === 'stop_limit'
                                        ? t('priceRequiredLimit')
                                        : false,
                                min: { value: 0, message: t('priceMustBePositive') },
                            })}
                            className="h-10 text-sm pr-20"
                        />
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                            <button
                                type="button"
                                onClick={() => handlePriceIncrement('price', true)}
                                className="w-8 h-8 flex items-center justify-center rounded bg-muted hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <ArrowUp className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePriceIncrement('price', false)}
                                className="w-8 h-8 flex items-center justify-center rounded bg-muted hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <ArrowDown className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                        <button
                            type="button"
                            onClick={() => {
                                const formattedPrice = formatInstrumentPrice(
                                    currentPrice,
                                    realTimePrice?.symbol
                                        ? { symbol: realTimePrice.symbol }
                                        : undefined
                                );
                                setValue('price', formattedPrice.replace(/,/g, ''));
                            }}
                            className="text-sm text-primary hover:text-primary bg-primary/10 dark:bg-primary/20 px-3 py-1.5 rounded-lg transition-colors border border-primary/20"
                        >
                            {t('current')}:{' '}
                            {formatInstrumentPrice(
                                currentPrice,
                                realTimePrice?.symbol ? { symbol: realTimePrice.symbol } : undefined
                            )}
                        </button>
                        {errors.price && (
                            <div className="flex items-center space-x-1 text-sm text-red-500">
                                <AlertCircle className="h-4 w-4" />
                                <span>{errors.price.message}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Stop Price Input */}
            {(orderType === 'stop' || orderType === 'stop_limit') && (
                <div>
                    <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                        {t('stopPrice')}
                    </Label>
                    <div className="relative">
                        <Input
                            type="number"
                            step="0.00001"
                            placeholder="0.00000"
                            {...register('stopPrice', {
                                required:
                                    orderType === 'stop' || orderType === 'stop_limit'
                                        ? t('stopPriceRequired')
                                        : false,
                                min: { value: 0, message: t('priceMustBePositive') },
                            })}
                            className="h-10 text-sm pr-20"
                        />
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                            <button
                                type="button"
                                onClick={() => handlePriceIncrement('stopPrice', true)}
                                className="w-8 h-8 flex items-center justify-center rounded bg-muted hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <ArrowUp className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePriceIncrement('stopPrice', false)}
                                className="w-8 h-8 flex items-center justify-center rounded bg-muted hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <ArrowDown className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                        <button
                            type="button"
                            onClick={() => {
                                const formattedPrice = formatInstrumentPrice(
                                    currentPrice,
                                    realTimePrice?.symbol
                                        ? { symbol: realTimePrice.symbol }
                                        : undefined
                                );
                                setValue('stopPrice', formattedPrice.replace(/,/g, ''));
                            }}
                            className="text-sm text-primary hover:text-primary bg-primary/10 dark:bg-primary/20 px-3 py-1.5 rounded-lg transition-colors border border-primary/20"
                        >
                            {t('current')}:{' '}
                            {formatInstrumentPrice(
                                currentPrice,
                                realTimePrice?.symbol ? { symbol: realTimePrice.symbol } : undefined
                            )}
                        </button>
                        {errors.stopPrice && (
                            <div className="flex items-center space-x-1 text-sm text-red-500">
                                <AlertCircle className="h-4 w-4" />
                                <span>{errors.stopPrice.message}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Stop Loss / Take Profit (optional protective levels) */}
            <div>
                <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                    {t('protectionLevels')}
                </Label>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Input
                            type="number"
                            step="0.00001"
                            min={0}
                            placeholder={t('stopLossPrice')}
                            {...register('stopLoss', {
                                min: { value: 0, message: t('priceMustBePositive') },
                            })}
                            className="h-10 text-sm"
                        />
                        {errors.stopLoss && (
                            <div className="flex items-center space-x-1 mt-1 text-sm text-red-500">
                                <AlertCircle className="h-4 w-4" />
                                <span>{errors.stopLoss.message}</span>
                            </div>
                        )}
                    </div>
                    <div>
                        <Input
                            type="number"
                            step="0.00001"
                            min={0}
                            placeholder={t('takeProfitPrice')}
                            {...register('takeProfit', {
                                min: { value: 0, message: t('priceMustBePositive') },
                            })}
                            className="h-10 text-sm"
                        />
                        {errors.takeProfit && (
                            <div className="flex items-center space-x-1 mt-1 text-sm text-red-500">
                                <AlertCircle className="h-4 w-4" />
                                <span>{errors.takeProfit.message}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Calculation Display */}
            {calculation && (
                <div
                    className={`p-4 rounded-lg bg-gradient-to-r from-primary/10 to-purple-500/10 dark:from-primary/20 dark:to-purple-500/20 border border-primary/20 dark:border-primary/30 space-y-3 transition-opacity ${
                        isCalculating ? 'opacity-70' : 'opacity-100'
                    }`}
                >
                    <div className="flex items-center space-x-2">
                        <Info className="h-4 w-4 text-primary dark:text-primary" />
                        <div className="text-sm font-semibold text-primary dark:text-primary">
                            {t('orderSummary')}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex justify-between items-center p-2 bg-white/50 dark:bg-white/5 rounded-lg border border-border dark:border-border">
                            <span className="text-muted-foreground">{t('marginRequired')}</span>
                            <span className="text-foreground font-mono font-semibold">
                                ${calculation.calculations?.margin_required?.toFixed(2) || '0.00'}
                            </span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-white/50 dark:bg-white/5 rounded-lg border border-border dark:border-border">
                            <span className="text-muted-foreground">{t('available')}</span>
                            <span className="text-foreground font-mono font-semibold">
                                ${selectedAccount?.free_margin?.toFixed(2) || '0.00'}
                            </span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-white/50 dark:bg-white/5 rounded-lg border border-border dark:border-border">
                            <span className="text-muted-foreground">{t('contractValue')}</span>
                            <span className="text-foreground font-mono font-semibold">
                                ${calculation.calculations?.contract_value?.toFixed(2) || '0.00'}
                            </span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-white/50 dark:bg-white/5 rounded-lg border border-border dark:border-border">
                            <span className="text-muted-foreground">{t('pipValue')}</span>
                            <span className="text-foreground font-mono font-semibold">
                                ${calculation.calculations?.pip_value?.toFixed(2) || '0.00'}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Submit Button */}
            <Button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={
                    isSubmitting ||
                    isPricesLoading ||
                    (isCrm && !selectedInstrumentId) ||
                    (isCrm && (!accountsList || accountsList.length === 0))
                }
                size="lg"
                className={`w-full h-12 font-semibold transition-all duration-200 hover:scale-105 ${
                    direction === 'buy'
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
            >
                {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>{isCrm ? 'Creating trade...' : t('placingOrder')}</span>
                    </div>
                ) : (
                    <div className="flex items-center space-x-2">
                        {direction === 'buy' ? (
                            <TrendingUp className="h-5 w-5" />
                        ) : (
                            <TrendingDown className="h-5 w-5" />
                        )}
                        <span>
                            {submitLabel ||
                                `${direction === 'buy' ? t('buy') : t('sell')} ${selectedPair}`}
                        </span>
                    </div>
                )}
            </Button>

            {/* Error display */}
            {pricesError && (
                <div className="text-center text-red-500 text-sm bg-red-50 dark:bg-red-950/20 px-3 py-2 rounded-lg border border-red-500/20">
                    {t('priceDataUnavailable')}
                </div>
            )}
        </div>
    );
};
